import { useEffect, useMemo, useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { promoteManufacturingMaterialLinesToUse } from "@/lib/manufacturing/materialUsesClient";
import { getManufacturingRecipeDetail } from "@/lib/manufacturing/recipesClient";
import type { MfgMaterial, MfgMaterialUse, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingComboboxPicker } from "./ManufacturingComboboxPicker";
import { useMaterialUseConvertPending } from "./useMaterialUseConvertPending";

type Preview = { lines: number };
type BrowseMode = "material" | "model";

const modeBtnBase =
  "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors";
const modeBtnInactive =
  "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700";
const modeBtnActive = "border-indigo-500 bg-indigo-600 text-white";
const convertBtnBase =
  "rounded-md px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60";
const convertBtnIdle =
  "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";
const convertBtnReady =
  "border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700";

export function ManufacturingMaterialUsePromoteLines({
  use,
  materials,
  recipes = [],
  memberMaterialIds = [],
  onPendingConvertChange,
  onDone,
}: {
  use: MfgMaterialUse;
  materials: readonly MfgMaterial[];
  recipes?: readonly MfgRecipeSummary[];
  memberMaterialIds?: readonly string[];
  onPendingConvertChange?: (pendingLines: number) => void;
  onDone?: () => void;
}) {
  const [browseMode, setBrowseMode] = useState<BrowseMode>("material");
  const [materialId, setMaterialId] = useState("");
  const [recipeId, setRecipeId] = useState("");
  const [modelMaterialIds, setModelMaterialIds] = useState<string[] | null>(null);
  const [modelLoadBusy, setModelLoadBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [selectedLines, setSelectedLines] = useState<number | null>(null);

  const pending = useMaterialUseConvertPending(
    use.id,
    memberMaterialIds,
    onPendingConvertChange,
  );

  const materialOptions = useMemo(
    () =>
      materials.map((m) => ({
        key: m.id,
        label: `${m.code} — ${m.name}`,
        sublabel: m.unit,
        data: m,
      })),
    [materials],
  );

  const recipeOptions = useMemo(
    () =>
      recipes
        .filter((r) => r.is_active && r.kind !== "variant" && r.kind !== "option")
        .map((r) => ({
          key: r.id,
          label: `${r.code} — ${r.name}`,
          sublabel: r.kind ?? "assembly",
          data: r,
        })),
    [recipes],
  );

  const modelMaterialOptions = useMemo(() => {
    if (!modelMaterialIds) return [];
    const allowed = new Set(modelMaterialIds);
    return materialOptions.filter((o) => allowed.has(o.key));
  }, [materialOptions, modelMaterialIds]);

  // Prefer a member material that still has convertible BOM lines.
  useEffect(() => {
    if (materialId) return;
    const firstPending = memberMaterialIds.find((id) => (pending.byMaterialId[id] ?? 0) > 0);
    if (firstPending) setMaterialId(firstPending);
  }, [memberMaterialIds, pending.byMaterialId, materialId]);

  useEffect(() => {
    if (browseMode !== "model" || !recipeId) {
      setModelMaterialIds(null);
      return;
    }
    let cancelled = false;
    setModelLoadBusy(true);
    setError(null);
    void (async () => {
      const r = await getManufacturingRecipeDetail(recipeId);
      if (cancelled) return;
      setModelLoadBusy(false);
      if (!r.ok) {
        setError(r.error);
        setModelMaterialIds([]);
        return;
      }
      setModelMaterialIds([
        ...new Set(
          r.data.lines
            .map((line) => line.material_id)
            .filter((id): id is string => Boolean(id)),
        ),
      ]);
    })();
    return () => {
      cancelled = true;
    };
  }, [browseMode, recipeId]);

  useEffect(() => {
    if (!materialId) {
      setSelectedLines(null);
      return;
    }
    const cached = pending.byMaterialId[materialId];
    if (typeof cached === "number") {
      setSelectedLines(cached);
      return;
    }
    let cancelled = false;
    void promoteManufacturingMaterialLinesToUse({
      from_material_id: materialId,
      material_use_id: use.id,
      dry_run: true,
    }).then((r) => {
      if (cancelled) return;
      setSelectedLines(r.ok ? r.data.lines : 0);
    });
    return () => {
      cancelled = true;
    };
  }, [materialId, use.id, pending.byMaterialId]);

  function setMode(next: BrowseMode) {
    setBrowseMode(next);
    setMaterialId("");
    setRecipeId("");
    setModelMaterialIds(null);
    setPreview(null);
    setError(null);
  }

  async function runDryRun() {
    if (!materialId) return;
    setBusy(true);
    setError(null);
    const r = await promoteManufacturingMaterialLinesToUse({
      from_material_id: materialId,
      material_use_id: use.id,
      dry_run: true,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setSelectedLines(r.data.lines);
    setPreview({ lines: r.data.lines });
  }

  async function confirmApply() {
    if (!materialId) return;
    setBusy(true);
    setError(null);
    const r = await promoteManufacturingMaterialLinesToUse({
      from_material_id: materialId,
      material_use_id: use.id,
      dry_run: false,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setPreview(null);
    setMaterialId("");
    await pending.rescan();
    onDone?.();
  }

  const sourceName = materials.find((m) => m.id === materialId)?.name ?? "material";
  const hasConvertible = (selectedLines ?? 0) > 0;
  const showConvertAlert = pending.pendingTotal > 0;

  return (
    <div
      className={
        "relative mb-4 rounded-lg border p-3 " +
        (showConvertAlert
          ? "border-amber-300 bg-amber-50/40 dark:border-amber-800 dark:bg-amber-950/20"
          : "border-gray-200 dark:border-zinc-700")
      }
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-gray-700 dark:text-zinc-300">
            Convert BOM references
          </p>
          <InlineHelp label="What Convert BOM references does" glyph="!" placement="right">
            <p>
              BOMs often pin one vendor SKU (for example Doorknob Co.). This rewrites those lines to
              this use (Doorknob) so builds can pick any interchangeable member.
            </p>
            <p className="mt-2">
              Search by <strong>Material</strong> in the catalog, or by <strong>Model</strong> to pick
              a material from a specific BOM. Conversion updates every recipe that still pins that
              material — not only the model you browsed.
            </p>
          </InlineHelp>
        </div>
        {showConvertAlert ? (
          <p className="max-w-[14rem] text-right text-[11px] font-semibold leading-snug text-amber-900 dark:text-amber-200">
            {pending.pendingTotal} BOM line{pending.pendingTotal === 1 ? "" : "s"} still pin member
            SKUs — click Convert
          </p>
        ) : null}
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-gray-500 dark:text-zinc-400">Find by:</span>
        <button
          type="button"
          className={`${modeBtnBase} ${browseMode === "material" ? modeBtnActive : modeBtnInactive}`}
          onClick={() => setMode("material")}
          disabled={busy}
        >
          Material
        </button>
        <button
          type="button"
          className={`${modeBtnBase} ${browseMode === "model" ? modeBtnActive : modeBtnInactive}`}
          onClick={() => setMode("model")}
          disabled={busy}
        >
          Model
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        {browseMode === "model" ? (
          <div className="min-w-[14rem] flex-1">
            <p className="mb-1 text-xs font-medium text-gray-600 dark:text-zinc-400">Model</p>
            <ManufacturingComboboxPicker<MfgRecipeSummary>
              value={recipeId}
              options={recipeOptions}
              onPick={(opt) => {
                setRecipeId(opt.key);
                setMaterialId("");
                setPreview(null);
              }}
              placeholder="Search models…"
              disabled={busy}
            />
          </div>
        ) : null}

        <div className="min-w-[14rem] flex-1">
          <p className="mb-1 text-xs font-medium text-gray-600 dark:text-zinc-400">
            {browseMode === "model" ? "Material on that BOM" : "Material"}
          </p>
          <ManufacturingComboboxPicker<MfgMaterial>
            value={materialId}
            options={browseMode === "model" ? modelMaterialOptions : materialOptions}
            onPick={(opt) => {
              setMaterialId(opt.key);
              setPreview(null);
            }}
            placeholder={
              browseMode === "model"
                ? modelLoadBusy
                  ? "Loading BOM materials…"
                  : recipeId
                    ? "Search materials on model…"
                    : "Pick a model first…"
                : "Search materials…"
            }
            disabled={
              busy ||
              (browseMode === "model" && (!recipeId || modelLoadBusy || modelMaterialOptions.length === 0))
            }
          />
        </div>

        <button
          type="button"
          className={`${convertBtnBase} ${hasConvertible ? convertBtnReady : convertBtnIdle}`}
          disabled={!materialId || busy}
          onClick={() => void runDryRun()}
        >
          {busy && !preview ? "Working…" : "Convert"}
        </button>
      </div>

      {browseMode === "model" && recipeId && !modelLoadBusy && modelMaterialOptions.length === 0 ? (
        <p className="mt-2 text-xs text-amber-800 dark:text-amber-300">
          That model has no direct material lines (only sub-recipes or uses). Pick another model, or
          switch to Material search.
        </p>
      ) : null}

      {error ? <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p> : null}

      {preview ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
          >
            <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Convert to use</h3>
            <p className="mt-2 text-xs text-gray-600 dark:text-zinc-300">
              Convert <span className="font-medium">{sourceName}</span> lines to use{" "}
              <span className="font-medium">{use.name}</span>. Affects{" "}
              <span className="font-medium">{preview.lines}</span> line
              {preview.lines === 1 ? "" : "s"} across every recipe that still pins this material.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className={ghostBtnClass}
                disabled={busy}
                onClick={() => setPreview(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800 disabled:opacity-60"
                disabled={busy || preview.lines === 0}
                onClick={() => void confirmApply()}
              >
                {busy ? "Applying…" : "Apply"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
